import type { IconType } from 'react-icons'
import {
  RiArrowDownSLine,
  RiCalendarEventFill,
  RiCheckboxLine,
  RiEqualizerLine,
  RiImageLine,
  RiListOrdered2,
  RiListUnordered,
  RiMenu4Fill,
  RiScalesLine,
  RiStarLine,
  RiTextSnippet,
  RiToggleLine,
  RiVideoLine,
} from 'react-icons/ri'
import type { QuestionTypeId } from './types'
import { CORE_QUESTION_TYPES } from './types'

/** Dev handoff tile glyph size (`5062:*` icon wells). */
export const QUESTION_TYPE_TILE_ICON_PX = 24

export interface QuestionTypeCatalogEntry {
  id: QuestionTypeId
  title: string
  description: string
  Icon: IconType
  /** Figma Dev well glyph size (defaults to {@link QUESTION_TYPE_TILE_ICON_PX}). */
  iconPixelSize?: number
  /** Selectable in US-IB-03 */
  enabled: boolean
}

/** Figma `4398:4850` / `5062:*` — [Remix Icon](https://remixicon.com/) instance names per tile. */
export const QUESTION_TYPE_CATALOG: readonly QuestionTypeCatalogEntry[] = [
  {
    id: 'MULTIPLE_CHOICE',
    title: 'Multiple Choice',
    description: 'Select multiple options',
    Icon: RiListUnordered,
    iconPixelSize: QUESTION_TYPE_TILE_ICON_PX,
    enabled: true,
  },
  {
    id: 'PICTURE_CHOICE',
    title: 'Picture Choice',
    description: 'Choose from image options',
    Icon: RiImageLine,
    iconPixelSize: QUESTION_TYPE_TILE_ICON_PX,
    enabled: true,
  },
  {
    id: 'SHORT_TEXT',
    title: 'Short Text',
    description: 'Single line text answer',
    Icon: RiTextSnippet,
    iconPixelSize: QUESTION_TYPE_TILE_ICON_PX,
    enabled: true,
  },
  {
    id: 'LONG_TEXT',
    title: 'Long Text',
    description: 'Multi-line text answer',
    Icon: RiMenu4Fill,
    iconPixelSize: QUESTION_TYPE_TILE_ICON_PX,
    enabled: true,
  },
  {
    id: 'YES_NO',
    title: 'Yes / No',
    description: 'Simple binary question',
    Icon: RiToggleLine,
    iconPixelSize: QUESTION_TYPE_TILE_ICON_PX,
    enabled: true,
  },
  {
    id: 'CHECKBOX',
    title: 'Checkbox',
    description: 'Pick a single option from a list',
    Icon: RiCheckboxLine,
    iconPixelSize: QUESTION_TYPE_TILE_ICON_PX,
    enabled: true,
  },
  {
    id: 'DATE',
    title: 'Date',
    description: 'Date picker input',
    Icon: RiCalendarEventFill,
    iconPixelSize: QUESTION_TYPE_TILE_ICON_PX,
    enabled: true,
  },
  {
    id: 'RATING',
    title: 'Rating',
    description: 'Star or number rating',
    Icon: RiStarLine,
    iconPixelSize: QUESTION_TYPE_TILE_ICON_PX,
    enabled: true,
  },
  {
    id: 'RANKING',
    title: 'Ranking',
    description: 'Order items by preference',
    Icon: RiListOrdered2,
    iconPixelSize: QUESTION_TYPE_TILE_ICON_PX,
    enabled: true,
  },
  {
    id: 'DROPDOWN',
    title: 'Dropdown',
    description: 'Select from dropdown menu',
    Icon: RiArrowDownSLine,
    iconPixelSize: QUESTION_TYPE_TILE_ICON_PX,
    enabled: true,
  },
  {
    id: 'LIKERT',
    title: 'Likert Scale',
    description: 'Agreement scale responses',
    Icon: RiScalesLine,
    iconPixelSize: QUESTION_TYPE_TILE_ICON_PX,
    enabled: true,
  },
  {
    id: 'SLIDER',
    title: 'Slider',
    description: 'Numeric slider input',
    Icon: RiEqualizerLine,
    iconPixelSize: QUESTION_TYPE_TILE_ICON_PX,
    enabled: true,
  },
  {
    id: 'VIDEO',
    title: 'Video',
    description: 'Play a video and collect a short text answer',
    Icon: RiVideoLine,
    iconPixelSize: QUESTION_TYPE_TILE_ICON_PX,
    enabled: true,
  },
] as const

export function isCoreType(id: QuestionTypeId): boolean {
  return (CORE_QUESTION_TYPES as readonly string[]).includes(id)
}
